import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFICES = [
  {
    city: "Austin",
    region: "Texas · United States",
    role: "Headquarters",
    focus: "Senior Partnership · Investment Committee",
  },
  {
    city: "New York",
    region: "United States",
    role: "Coverage · Active",
    focus: "Capital Markets Liaison · North American LP Coverage",
  },
  {
    city: "London",
    region: "United Kingdom",
    role: "Coverage · Active",
    focus: "European Origination · Limited Partner Coverage",
  },
  {
    city: "Geneva",
    region: "Switzerland",
    role: "Coverage · Active",
    focus: "Private Wealth Dialogue · Stewardship Coverage",
  },
  {
    city: "Dubai",
    region: "United Arab Emirates · DIFC",
    role: "Coverage · Active",
    focus: "Gulf Region · UAE & GCC Investor Coverage · Life Sciences Dialogue",
  },
  {
    city: "Singapore",
    region: "Singapore",
    role: "Coverage · Active",
    focus: "Asia-Pacific LP Coverage · Family Office Dialogue",
  },
  {
    city: "Hong Kong",
    region: "Hong Kong SAR",
    role: "Coverage · Active",
    focus: "Greater China Coverage · Strategic Capital Relationships",
  },
];

const CHANNELS = [
  {
    label: "Investor Relations",
    email: "ir@krprivatecapital.com",
  },
  {
    label: "Investor & Strategic Capital",
    email: "investors@krprivatecapital.com",
  },
  {
    label: "Media & Press Enquiries",
    email: "investors@krprivatecapital.com",
  },
  {
    label: "Counsel & Counterparties",
    email: "investors@krprivatecapital.com",
  },
];

const ContactPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    firm: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", message: "Please complete required fields." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        message:
          "Thank you. Your enquiry has been received. The partnership will respond where appropriate.",
      });
      setForm({
        full_name: "",
        email: "",
        firm: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          "We could not transmit your enquiry. Please retry, or write to ir@krprivatecapital.com.",
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.contactPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Contact"
          label="General enquiries · Press · Counterparties"
          title={
            <>
              Initiate a{" "}
              <span className="italic text-[var(--kr-gold)]">documented</span>{" "}
              correspondence.
            </>
          }
          description="For general correspondence, press enquiries and counterparty introductions. Investor access requests are handled through the dedicated Investor Access channel."
          breadcrumb="Contact"
        />

        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="space-y-10">
                {CHANNELS.map((c) => (
                  <div key={c.label}>
                    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                      {c.label}
                    </div>
                    <a
                      href={`mailto:${c.email}`}
                      className="kr-link font-serif-display text-[1.2rem]"
                    >
                      {c.email}
                    </a>
                  </div>
                ))}
                <div className="kr-rule" />
                <div>
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    Engagement Posture
                  </div>
                  <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85] max-w-[42ch]">
                    The partnership engages on a documented, confidential basis.
                    Capital relationships are advanced selectively, by
                    introduction, with the partnership&apos;s underwriting standards
                    as the baseline.
                  </p>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={openModal}
                    className="kr-btn-ghost"
                  >
                    Request Investor Access →
                  </button>
                </div>
              </div>
            </div>

            <form
              onSubmit={submit}
              data-testid={LANDING.contactPageForm}
              className="lg:col-span-7 lg:pl-10"
            >
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mb-10">
                <div>
                  <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={update("full_name")}
                    data-testid={LANDING.contactPageName}
                    className="kr-input"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid={LANDING.contactPageEmail}
                    className="kr-input"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                    Firm / Institution (Optional)
                  </label>
                  <input
                    type="text"
                    value={form.firm}
                    onChange={update("firm")}
                    data-testid={LANDING.contactPageFirm}
                    className="kr-input"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    data-testid={LANDING.contactPageSubject}
                    className="kr-input"
                  />
                </div>
              </div>
              <div className="mb-10">
                <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                  Message *
                </label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  data-testid={LANDING.contactPageMessage}
                  className="kr-input resize-none"
                />
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid={LANDING.contactPageSubmit}
                  className="kr-btn-primary disabled:opacity-60"
                >
                  {status.state === "loading" ? "Sending…" : "Send Enquiry"}
                  <span aria-hidden>→</span>
                </button>
                {status.message && (
                  <p
                    data-testid={LANDING.contactPageStatus}
                    className={`text-[14px] max-w-[42ch] ${
                      status.state === "success"
                        ? "text-[var(--kr-gold)]"
                        : "text-[#d27a7a]"
                    }`}
                  >
                    {status.message}
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>

        {/* Offices */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Global Footprint
              </span>
              <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              <span className="font-mono-label text-[11px] text-[var(--kr-text-muted)]">
                Headquarters + six coverage locations
              </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {OFFICES.map((o) => (
                <article
                  key={o.city}
                  data-testid={`${LANDING.contactPageOfficePrefix}${o.city.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500 ${
                    o.role === "Headquarters" ? "lg:col-span-2" : ""
                  }`}
                  style={
                    o.role === "Headquarters"
                      ? { background: "rgba(201,169,110,0.04)" }
                      : {}
                  }
                >
                  <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                    {o.role}
                  </div>
                  <h3 className="font-serif-display text-[1.9rem] text-[var(--kr-text)] mb-2 leading-[1.1]">
                    {o.city}
                  </h3>
                  <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[0.95rem] mb-6">
                    {o.region}
                  </div>
                  <div className="kr-rule mb-4" />
                  <p className="text-[var(--kr-text)] text-[13.5px] leading-[1.65]">
                    {o.focus}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default ContactPage;
